import { GRAPHQL_BACKEND_URL } from "@/constants";
import { GraphQLError } from "graphql";
import React from "react";

import { createApolloCache } from "@/apollo/cache";
import { isDevelopment } from "@/helpers/environment";
import { ApolloClient, ApolloProvider, HttpLink, from } from "@apollo/client";
import { NetworkError } from "@apollo/client/errors";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { useAuth0 } from "@auth0/auth0-react";

import { getCurrentAccountIdScope } from "./account/accountScope";

/* eslint-disable no-console */
function logGraphQLError(error: GraphQLError) {
  console.error("[GraphQL Error]", error.message, error);
}

function logNetworkError(error: NetworkError) {
  console.error("[NetworkError]", error?.message, error);
}
/* eslint-enable no-console */

/**
 * The two context links, in the order they must run.
 *
 * `authLink` awaits a token, and every await is a window in which the account
 * scope can change — a workspace switch, or simply the scope being published
 * for the first time during boot. Stamping `x-account-id` first pins the
 * request to the account it was issued for, rather than to whichever account
 * happens to be current by the time the token resolves.
 *
 * Exported so that the ordering guarantee can be tested; nothing else should
 * compose these.
 */
export function createContextLinks(
  getAccessToken: () => Promise<string | undefined>,
) {
  const accountIdLink = setContext((_, prevContext) => {
    if (prevContext.skipXAccountIdHeader === true) {
      return prevContext;
    }
    const currentAccountId: string | null =
      prevContext.accountId || getCurrentAccountIdScope();
    if (!currentAccountId) {
      return prevContext;
    }
    return {
      ...prevContext,
      headers: {
        ...prevContext.headers,
        "x-account-id": currentAccountId,
      },
    };
  });

  const authLink = setContext(async (_, { headers }) => {
    const token = await getAccessToken();
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  });

  return [accountIdLink, authLink];
}

export default function Provider({ children }: React.PropsWithChildren) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  // Both dependencies are `useCallback(…, [client])` inside auth0-react, so this
  // memo resolves once. Keep it that way: recreating the client would hand the
  // app a fresh `createApolloCache()` and silently drop everything cached so far.
  const client = React.useMemo(() => {
    // Retry failed requests in production only. In development, retries interfere
    // with backend breakpoint debugging (a retry fires mid-step and the debugger
    // hops between requests).
    // Default configuration: 5 retries, 300ms delay, exponential backoff.
    // https://www.apollographql.com/docs/react/api/link/apollo-link-retry/#default-configuration
    const retryLink = isDevelopment ? null : new RetryLink();

    const httpLink = new HttpLink({
      uri: `${GRAPHQL_BACKEND_URL}/graphql`,
      credentials: "include",
      // A build constant, so it belongs here rather than being recomputed by a
      // link on every request. Context headers still win over these.
      headers: { "x-app-release-version": import.meta.env.RELEASE_ID || "" },
    });

    const contextLinks = createContextLinks(getAccessTokenSilently);

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach((error) => {
          if (isDevelopment) {
            logGraphQLError(error);
          }
          if (error.extensions?.code === 441) {
            window.location.href =
              "/email-not-verified" + window.location.search;
          }
          const originalError = error.extensions?.originalError;
          if (!originalError) {
            return;
          }
          if (
            typeof originalError === "object" &&
            "statusCode" in originalError &&
            originalError.statusCode === 401 &&
            "message" in originalError &&
            originalError.message === "Email not validated"
          ) {
            window.location.href = "/email-not-valid" + window.location.search;
          }
        });
      }

      if (networkError) {
        if (isDevelopment) {
          logNetworkError(networkError);
        }
        const error = (networkError as any).error;
        if (error === "login_required" || error === "invalid_grant") {
          loginWithRedirect({
            authorizationParams: { prompt: "login" },
          });
          return;
        }
      }
    });

    const apolloClient = new ApolloClient({
      cache: createApolloCache(),
      link: from(
        [errorLink, retryLink, ...contextLinks, httpLink].filter(
          (link): link is NonNullable<typeof link> => link !== null,
        ),
      ),
      connectToDevTools: isDevelopment,
    });

    return apolloClient;
  }, [getAccessTokenSilently, loginWithRedirect]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
